import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { languages } from "../i18n/translations";

export const LanguageSelector = ({ variant = "desktop" }) => {
  const { lang, setLang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];

  const handleSelect = (code) => {
    setLang(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-[#009A49]/50"
        data-testid="language-toggle"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span className="font-body text-xs font-semibold tracking-wider">{current.short}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-44 bg-[#0A0A0A] border border-white/10 rounded-sm shadow-2xl overflow-hidden z-50"
            data-testid="language-dropdown"
          >
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => handleSelect(l.code)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-body transition-colors ${
                  lang === l.code
                    ? "text-[#009A49] bg-white/5"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`language-option-${l.code}`}
              >
                <span>{l.label}</span>
                {lang === l.code && <Check className="w-4 h-4" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
